import { useCallback, useEffect, useState } from "react";
import { BookOpen, Eye, EyeOff, LoaderCircle, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { title: "", section: "", body: "", source_label: "", source_url: "", last_checked_at: "", published: false };

function ContentModal({ initial, sections, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    title: initial.title, section: initial.section, body: initial.body,
    source_label: initial.source_label || "", source_url: initial.source_url || "",
    last_checked_at: initial.last_checked_at || "", published: initial.published,
  } : { ...EMPTY, section: sections[0] });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, source_label: form.source_label || null, source_url: form.source_url || null, last_checked_at: form.last_checked_at || null };
      if (initial) await api.patch(`/admin/contents/${initial.id}`, payload);
      else await api.post("/admin/contents", payload);
      toast.success(initial ? "Contenu mis à jour." : "Contenu créé.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer le contenu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={onClose} data-testid="admin-content-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-lg font-extrabold text-slate-900">{initial ? "Modifier le contenu" : "Nouveau contenu"}</h3>
          <button type="button" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="admin-content-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <input value={form.title} onChange={set("title")} required minLength={2} maxLength={160} placeholder="Titre du contenu"
          className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="admin-content-title-input" />
        <select value={form.section} onChange={set("section")}
          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="admin-content-section-select">
          {sections.map((s) => <option key={s}>{s}</option>)}
        </select>
        <textarea value={form.body} onChange={set("body")} required minLength={10} maxLength={8000} rows={7}
          placeholder="Corps du contenu pédagogique…"
          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none" data-testid="admin-content-body-input" />
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input value={form.source_label} onChange={set("source_label")} maxLength={160} placeholder="Source officielle (ex. Service-Public.fr)"
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="admin-content-source-label-input" />
          <input value={form.source_url} onChange={set("source_url")} maxLength={300} placeholder="URL de la source"
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="admin-content-source-url-input" />
        </div>
        <label className="mt-3 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Dernière vérification de la source</label>
        <input type="date" value={form.last_checked_at} onChange={set("last_checked_at")}
          className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="admin-content-checked-input" />
        <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
            className="h-4 w-4 rounded border-slate-300 text-bleu" data-testid="admin-content-published-checkbox" />
          Publié (visible par les utilisateurs dans Formations & Ressources)
        </label>
        <button type="submit" disabled={saving} className="btn-primary mt-5 w-full !py-3 disabled:opacity-50" data-testid="admin-content-save">
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : initial ? "Enregistrer les modifications" : "Créer le contenu"}
        </button>
      </form>
    </div>
  );
}

export default function AdminContents() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/contents").then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les contenus."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePublish = async (c) => {
    try {
      await api.patch(`/admin/contents/${c.id}`, { published: !c.published });
      toast.success(c.published ? "Contenu dépublié." : "Contenu publié.");
      load();
    } catch { toast.error("Impossible de modifier la publication."); }
  };

  const remove = async () => {
    const c = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/admin/contents/${c.id}`);
      toast.success("Contenu supprimé.");
      load();
    } catch { toast.error("Impossible de supprimer le contenu."); }
  };

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="page-admin-contents">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Contenus pédagogiques</h1>
          <p className="mt-1 text-sm text-slate-500">Créez, publiez et tenez à jour les contenus visibles dans « Formations & Ressources ».</p>
        </div>
        <button onClick={() => setModal({ mode: "create" })} className="btn-primary !px-5 !py-2.5 !text-xs" data-testid="admin-content-create-button">
          <Plus className="h-3.5 w-3.5" /> Nouveau contenu
        </button>
      </div>

      {data.contents.length === 0 ? (
        <div className="card-integra p-10 text-center" data-testid="admin-contents-empty">
          <BookOpen className="mx-auto h-10 w-10 text-slate-200" />
          <p className="mt-3 text-sm font-bold text-slate-700">Aucun contenu pour le moment</p>
          <p className="mt-1 text-xs text-slate-400">Créez votre premier contenu pédagogique : il apparaîtra dans l'espace des utilisateurs une fois publié.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.contents.map((c) => (
            <div key={c.id} className="card-integra p-4" data-testid="admin-content-row">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-bleu-light px-2.5 py-1 text-[10px] font-bold text-bleu">{c.section}</span>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${c.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="admin-content-status-chip">
                  {c.published ? "Publié" : "Brouillon"}
                </span>
                {c.last_checked_at && <span className="text-[10px] text-slate-400">Source vérifiée le {new Date(c.last_checked_at).toLocaleDateString("fr-FR")}</span>}
                <div className="ml-auto flex items-center gap-1.5">
                  <button onClick={() => togglePublish(c)} title={c.published ? "Dépublier" : "Publier"}
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-bleu/40 hover:text-bleu" data-testid="admin-content-publish-toggle">
                    {c.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => setModal({ mode: "edit", content: c })} title="Modifier"
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-bleu/40 hover:text-bleu" data-testid="admin-content-edit-button">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setToDelete(c)} title="Supprimer"
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-rouge/40 hover:text-rouge" data-testid="admin-content-delete-button">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm font-bold text-slate-900">{c.title}</p>
              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{c.body}</p>
              {c.source_label && <p className="mt-1.5 text-[11px] text-slate-400">Source : {c.source_label}{c.source_url && ` — ${c.source_url}`}</p>}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ContentModal initial={modal.mode === "edit" ? modal.content : null} sections={data.sections}
          onClose={() => setModal(null)} onSaved={load} />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="admin-content-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contenu ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {toDelete?.title} » sera définitivement supprimé
              {toDelete?.published && " et ne sera plus visible par les utilisateurs"}. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-content-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rouge text-white hover:bg-red-700" data-testid="admin-content-delete-confirm">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
